/**
 * Однократный запрос разрешения на уведомления при заходе на сайт.
 *
 * ПОСТАНОВКА АНТОНА (2026-09-10 14:49): «колокольчик там на самом деле не
 * нужен, ты должен один раз при рефреше сайта спрашивать — разрешить
 * уведомления или нет». То есть вопрос задаём сами, сразу, без поиска
 * кнопок в интерфейсе.
 *
 * ГЛАВНАЯ ТРУДНОСТЬ: браузеры (и Safari строже всех) показывают нативный
 * диалог ТОЛЬКО из обработчика жеста человека. Вызов из useEffect при
 * загрузке Safari игнорирует МОЛЧА — именно поэтому «даже запроса нет».
 * Поэтому схема такая: при загрузке подписываемся на первый же клик или
 * нажатие клавиши и запрашиваем разрешение внутри него. Для человека это
 * выглядит как «зашёл, щёлкнул — сразу спросили».
 *
 * Спрашиваем ОДИН раз за сессию страницы и только при permission="default":
 * повторно дёргать бессмысленно (браузер запомнил ответ) и навязчиво.
 */


import { loadNotificationSettings, saveNotificationSettings } from "./settings";

export function askPermissionOnFirstGesture(onResult?: (p: NotificationPermission) => void): void {
  if (typeof Notification === "undefined") return;
  // Уже решено — не трогаем. denied тоже решение: переспрашивать нельзя,
  // браузер всё равно откажет без нового жеста в своих настройках.
  if (Notification.permission !== "default") return;
  // БАГ, найденный проверкой Антона 14:54 («запроса нет после рефреша»):
  // раньше здесь стоял флаг в localStorage «уже спрашивали», и он ставился
  // ДО получения ответа. Если Safari диалог не показал (жест не засчитан,
  // настройка браузера, что угодно) — флаг всё равно оставался, и запрос
  // не повторялся НИКОГДА, даже после перезагрузки. Флаг убран совсем:
  // Антон просил спрашивать «один раз при рефреше», то есть один раз на
  // загрузку страницы, а не один раз на всю жизнь браузера. Пока
  // permission === "default", каждая перезагрузка даёт новую попытку;
  // как только человек ответил, браузер сам больше не спросит.

  const ask = () => {
    window.removeEventListener("pointerdown", ask);
    window.removeEventListener("keydown", ask);
    const apply = (p: NotificationPermission) => {
      // Разрешил в браузере — значит показ включён. Иначе человек ответил
      // «да» системе, а внутренний выключатель остался в «нет», и
      // уведомления по-прежнему не приходят (баг, найденный 15:31).
      if (p === "granted") {
        saveNotificationSettings({ ...loadNotificationSettings(), enabled: true });
      }
      onResult?.(p);
    };
    try {
      // Safari отдаёт результат КОЛЛБЭКОМ и промиса не возвращает;
      // Chrome/Firefox — промис. Поддерживаем оба, не доверяя типам.
      const maybe = Notification.requestPermission(apply) as
        | Promise<NotificationPermission>
        | undefined;
      if (maybe && typeof maybe.then === "function") void maybe.then(apply).catch(() => {});
    } catch {
      // Браузер отказал — молчим, у человека остаётся ручной путь.
    }
  };

  window.addEventListener("pointerdown", ask);
  window.addEventListener("keydown", ask);
}

